import FocusManager from '../utils/focus-manager';
import ScrollManager from '../utils/scroll-manager';

export default function initHeaderNavigation() {
    new Header(document.getElementById('main-header'));
}

/**
 * @param {HTMLElement}       $el                                  HTML Element header
 * @param {HTMLElement}       $body                                HTML Body
 * @param {HTMLButtonElement} $menuToggler                         Toggler du menu
 * @param {HTMLElement}       $mainHeaderMenu                      Le menu principal
 * @param {HTMLElement}       $mainHeaderSubmenuBtnReturn          Le menu retour pour mobile
 * @param {integer}           mobileBreakpoint                     Breakpoint mobile
 * @param {string}            initializedDevice                    Nom du device initialisé (mobile | desktop)
 * @param {Element[]}         $openedPanels                        Liste des panneaux ouverts
 * 
 */
class Header {
    $el : HTMLElement | null;
    $body : HTMLElement | null;
    $menuToggler : HTMLButtonElement | null | undefined;
    $mainHeaderNav : HTMLElement | null | undefined;
    $mainHeaderMenu : HTMLElement | null | undefined;
    $mainHeaderSubmenuBtnReturn : NodeListOf<HTMLElement> | null | undefined;
    mobileBreakpoint : number;
    initializedDevice : string | null;
    $isAnchorNav : HTMLElement | null | undefined;
    $openedPanels : Element[];
    $activeButtonSubMenu : HTMLButtonElement | null;
    $submenuTabs: NodeListOf<HTMLElement> | null | undefined;
    $submenuTabsContent: NodeListOf<HTMLElement> | null | undefined;
    $loginButton: HTMLButtonElement | null | undefined;

    constructor($el : HTMLElement | null) {
        this.$el = $el;
        this.$body = document.querySelector('body');
        this.$menuToggler = $el?.querySelector('.js-main-header-burger-button');
        this.$mainHeaderNav = $el?.querySelector('.js-main-header-navigation');
        this.$mainHeaderMenu = $el?.querySelector('.js-main-header-menu');
        this.$mainHeaderSubmenuBtnReturn = $el?.querySelectorAll('.js-btn-submenu-return');
        this.mobileBreakpoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-main-header')) + 1;
        this.initializedDevice = null;
        this.$openedPanels = [];
        this.$activeButtonSubMenu = null;
        this.$submenuTabs = $el?.querySelectorAll('.submenu-item-tab');
        this.$submenuTabsContent = $el?.querySelectorAll('.submenu-item-tab-content');
        this.$loginButton = $el?.querySelector('.cta-account');

        this.init();
        window.addEventListener('resize', this.init.bind(this));

        // Initialiser le gestionnaire de clic pour le bouton de connexion directement dans le constructeur
        if (this.$loginButton) {
            this.$loginButton.addEventListener('click', this.handleLoginButtonClick.bind(this));
        }
        
        // Initialiser le gestionnaire de touche Escape au niveau global
        document.addEventListener('keydown', this.handleDocumentKeyDown.bind(this));

        // Ajouter l'écouteur de clic sur le document
        document.addEventListener('click', this.handleDocumentClick.bind(this));

        // Déplacer l'initialisation des onglets dans le constructeur
        this.$submenuTabs?.forEach(($tab) => {
            $tab.addEventListener('click', (event) => this.handleTabClick(event));
        });
    }

    /**
     * Initialise le header dans le bon device en fonction
     * de la taille de l'écran (mobile ou desktop)
     *
     * @return {void}
     */
    init() {
        if (window.innerWidth < this.mobileBreakpoint && this.initializedDevice !== 'mobile') {
            this.initMobile();
        } else if (window.innerWidth >= this.mobileBreakpoint && this.initializedDevice !== 'desktop') {
            this.initDesktop();
        }

        this.initStickyNavigation();
        window.addEventListener('scroll', () => {
            this.initStickyNavigation();
        });
    }

    /**
     * Initialise le header mobile
     *
     * @return {void}
     */
    initMobile() {
        this.initializedDevice = 'mobile';
        this.destroyDesktop();

        // Ouverture / Fermeture du menu au clique sur le toggler du menu
        if (this.$menuToggler) {
            this.$menuToggler.addEventListener('click', this.mobileClickMenuTogglerHandler.bind(this));
        }
        // Fermeture du menu à l'appuie sur la touche Escape
        document.addEventListener('keydown', this.mobileKeyDownDocumentHandler.bind(this));

        // Fermeture sous-menu au clique sur les boutons retour
        if (this.$mainHeaderSubmenuBtnReturn) {
            this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
                $link.addEventListener('click', this.mobileClickMainHeaderPhoneNavHandler);
            });
        }

        // Ouverture sous-menu au clique sur un élément de menu contenant un sous-menu
        this.$mainHeaderMenu?.querySelectorAll('.menu-item-has-submenu').forEach($link => {

            $link.addEventListener('click', (evt) => {
                this.mobileClickMainHeaderNavLinkWithChildrenHandler(evt);
            });
        });
    }

    /**
     * Fonction à éxécuter au clique sur le toggler du menu
     *
     * @return {void}
     */
    mobileClickMenuTogglerHandler() {
        if (this.$el?.classList.contains('open')) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    /**
     * Fonction à éxécuter à l'appuie sur une touche du clavier
     *
     * @return {void}
     */
    mobileKeyDownDocumentHandler(evt: KeyboardEvent) {
        if (evt.key === 'Escape' && this.$el?.classList.contains('open')) {
            this.closeMobileMenu(true);
        }
    }

    /**
     * Fonction à éxécuter au clique sur un lien du menu contenant des enfants
     *
     * @return {void}
     */
    mobileClickMainHeaderNavLinkWithChildrenHandler(evt: Event) {
        evt.preventDefault();
        const $item = (evt.target as Element)?.nextElementSibling;

        if ($item) {
            this.openMobileSubmenu($item);
        }
    }

    /**
     * Fonction à éxécuter au clique sur un lien de retour
     *
     * @return {void}
     */
    mobileClickMainHeaderPhoneNavHandler = () => {
        this.closeMobileMenu(true);
    }

    /**
     * Initialise le header desktop
     *
     * @return {void}
     */
    initDesktop() {
        this.destroyMobile();
        this.initializedDevice = 'desktop';

        this.initSubMenus();

        document.addEventListener('keydown', this.handleDocumentKeyDown.bind(this));
    }
    
    /**
     * Initialise les event clavier sur le menu desktop
     *
     * @return {void}
     */
    handleSubMenuKeyDown(event: KeyboardEvent) {
        const subMenuBtn = event.target as HTMLButtonElement;
        const keyboardEvent = event as KeyboardEvent;
        if (
            keyboardEvent.key === ' ' ||
            keyboardEvent.key === 'Spacebar' ||
            keyboardEvent.keyCode === 32 ||
            keyboardEvent.keyCode === 13 ||
            keyboardEvent.key === 'Enter'
        ) {
            // Barre d'espace enfoncée
            keyboardEvent.preventDefault(); // Empêche le défilement de la page
            if (subMenuBtn !== this.$activeButtonSubMenu) {
                // Un autre bouton a actuellement la classe active
                if (this.$activeButtonSubMenu) {
                    this.$activeButtonSubMenu.classList.remove('active');
                }
                subMenuBtn?.classList.add('active');
                this.$activeButtonSubMenu = subMenuBtn;
            } else {
                subMenuBtn?.classList.remove('active');
                this.$activeButtonSubMenu = null;
            }
        }
    }

    /**
     * Initialise les event clavier sur le bouton esc pour fermer les sous menu
     *
     * @return {void}
     */
    handleDocumentKeyDown(event : KeyboardEvent) {
        const keyboardEvent = event as KeyboardEvent;
        const subMenusBtns = this.$el?.querySelectorAll('.menu-item');
        if (keyboardEvent.key === 'Escape' || keyboardEvent.key === 'Esc' || keyboardEvent.keyCode === 27) {
            subMenusBtns?.forEach((subMenuBtn) => {
                subMenuBtn.classList.remove('active');
                this.$activeButtonSubMenu = null;
            });
            
            // Ajouter la suppression de la classe active du bouton de connexion
            this.$loginButton?.classList.remove('active');
        }
    }

    /**
     * Gère le clic sur un onglet
     * 
     * @param {Event} event 
     * @return {void}
     */
    handleTabClick(event: Event) {
        const tab = event.currentTarget as HTMLElement;
        const tabId = tab.dataset.tab;

        // Désactive tous les onglets et contenus
        this.$submenuTabs?.forEach(($tab) => {
            $tab.classList.remove('active');
        });

        this.$submenuTabsContent?.forEach(($content) => {
            $content.classList.remove('active');
        });

        // Active l'onglet cliqué et son contenu
        tab.classList.add('active');
        const targetContent = this.$el?.querySelector(`[data-tab-target="${tabId}"]`);
        targetContent?.classList.add('active');

        // Met le focus sur l'onglet actif
        tab.focus();
    }

    /**
     * Gère le clic sur le bouton de connexion
     * 
     * @return {void}
     */
    handleLoginButtonClick() {
        this.$loginButton?.classList.toggle('active');
    }

    /**
     * Détruit le header mobile
     *
     * @return {void}
     */
    destroyMobile() {
        // On ré-autorise le scroll dans la page
        ScrollManager.preventScroll(false);

        // On enlève la contrainte du focus
        FocusManager.untrapFocus();

        // On ferme le menu s'il était ouvert pour éviter les erreurs
        this.$el?.classList.remove('open');
        this.$body?.classList.remove('menu-is-open');

        // Suppression des events bind lors de l'initialisation du header mobile
        if (this.$menuToggler) {
            this.$menuToggler.removeEventListener('click', this.mobileClickMenuTogglerHandler);
        }

        document.removeEventListener('keydown', this.mobileKeyDownDocumentHandler);
        if (this.$mainHeaderSubmenuBtnReturn) {
            this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
                $link.removeEventListener('click', this.mobileClickMainHeaderPhoneNavHandler);
            });
        }

        this.$mainHeaderMenu?.querySelectorAll('.menu-item-has-submenu').forEach($link => {
            $link.removeEventListener('click', () => {
                this.mobileClickMainHeaderNavLinkWithChildrenHandler;
            });
        });

        this.$mainHeaderMenu?.querySelectorAll('.menu-item-submenu.open').forEach($item => {
            $item.classList.remove('open');
        });
    }

    /**
     * Détruit le header desktop
     *
     * @return {void}
     */
    destroyDesktop() {
        const subMenusBtns = this.$el?.querySelectorAll('.menu-item-has-submenu-opener');
        
        // Suppression des écouteurs d'événements
        subMenusBtns?.forEach((btn) => {
            btn.removeEventListener('click', (e) => {
                e.preventDefault();
                const menuItem = (e.currentTarget as HTMLElement).closest('.menu-item');
                const menuItemLink = menuItem?.querySelector('.menu-item-link');

                // Réinitialiser tous les autres menus
                this.$el?.querySelectorAll('.menu-item.open').forEach((item) => {
                    if (item !== menuItem) {
                        item.classList.remove('open');
                        item.querySelector('.menu-item-link')?.classList.remove('active');
                    }
                });

                // Basculer l'état du sous-menu actuel
                menuItem?.classList.toggle('open');
                menuItemLink?.classList.toggle('active');
            });
        });

        document.removeEventListener('keydown', this.handleDocumentKeyDown.bind(this));
    }

    /**
     * Initialise le header sticky
     *
     * @return {void}
     */
    initStickyNavigation() {
        if (window.pageYOffset > 20) {
            this.$el?.classList.add('main-header--sticky');
            this.$body?.classList.add('main-header--is-sticky');
        } else {
            this.$el?.classList.remove('main-header--sticky');
            this.$body?.classList.remove('main-header--is-sticky');
        }
    }

    /**
     * Ouvre le menu mobile
     *
     * @return {void}
     */
    openMobileMenu() {
        this.$el?.classList.add('open');
        this.$body?.classList.add('menu-is-open');

        // ScrollManager
        //     .scrollTo(0)
        //     .preventScroll(true);

        ScrollManager.preventScroll(true);
        
        FocusManager.trapFocus(
            [this.$el]
        );
    }

    /**
     * Ferme le menu mobile
     *
     * @param {boolean} onlyLastPanel Seulement le dernier panneau ouvert
     * @return {void}
     */
    closeMobileMenu(onlyLastPanel: boolean = false): void {
        if (onlyLastPanel && this.$openedPanels.length > 0) {
            this.closeMobileSubmenu();
        } else {
            this.$el?.classList.remove('open');
            this.$mainHeaderNav?.classList.remove('submenu-is-open');
            this.$body?.classList.remove('menu-is-open');

            this.$openedPanels.forEach($panel => {
                $panel.classList.remove('open');
            });
            this.$openedPanels = [];

            // On remet le scroll de la page
            ScrollManager.preventScroll(false);

            // On enlève la contrainte du focus
            FocusManager.untrapFocus();
        }
    }

    /**
     * Ouvre un sous-menu mobile
     *
     * @param {Element} $item
     * @param {string}      $itemLabel
     * @return {void}
     */
    openMobileSubmenu($item : Element) {
        $item?.classList.add('open');

        this.$openedPanels?.push($item);

        this.$mainHeaderNav?.classList.add('submenu-is-open');
    }

    /**
     * Ferme le dernier sous-menu mobile ouvert
     *
     * @return {void}
     */
    closeMobileSubmenu() {
        const $item = this.$openedPanels?.pop();
        $item?.classList.remove('open');
        this.$mainHeaderNav?.classList.remove('submenu-is-open');
    }

    /**
     * Gère le clic sur le document pour fermer le menu de connexion
     * si on clique à l'extérieur
     * 
     * @param {MouseEvent} event 
     * @return {void}
     */
    handleDocumentClick(event: MouseEvent) {
        const target = event.target as Node;
        
        // Vérifier si le clic est en dehors du bouton de connexion et de son menu
        if (this.$loginButton?.classList.contains('active') &&
            !this.$loginButton.contains(target) &&
            !this.$loginButton.parentElement?.querySelector('.main-header-login-menu')?.contains(target)) {
            this.$loginButton.classList.remove('active');
        }
    }

    destroy() {
        // Ajouter une méthode de nettoyage globale
        if (this.$loginButton) {
            this.$loginButton.removeEventListener('click', this.handleLoginButtonClick);
        }
        document.removeEventListener('keydown', this.handleDocumentKeyDown);
        document.removeEventListener('click', this.handleDocumentClick);

        // Ajouter le nettoyage des écouteurs des onglets
        this.$submenuTabs?.forEach(($tab) => {
            $tab.removeEventListener('click', (event) => this.handleTabClick(event));
        });
    }

    private initSubMenus(): void {
        const subMenusBtns = this.$el?.querySelectorAll('.menu-item-has-submenu-opener');
        
        subMenusBtns?.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const menuItem = (e.currentTarget as HTMLElement).closest('.menu-item');

                // Réinitialiser tous les autres menus
                this.$el?.querySelectorAll('.menu-item.active').forEach((item) => {
                    if (item !== menuItem) {
                        item.classList.remove('active');
                    }
                });

                // Basculer l'état du sous-menu actuel
                menuItem?.classList.toggle('active');
            });
        });

        // Fermer les sous-menus lors d'un clic à l'extérieur
        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.menu-item-has-submenu')) {
                this.$el?.querySelectorAll('.menu-item.active').forEach((item) => {
                    item.classList.remove('active');
                });
            }
        });
    }
}
